// Fuzzball Quiz Unified Products Manager
// Manages and displays both trial box and extras selections in a unified list

class UnifiedProductsManager {
  constructor() {
    this.products = {
      cat1: { trail: [], extras: [], bundle: [] },
      cat2: { trail: [], extras: [], bundle: [] },
      cat3: { trail: [], extras: [], bundle: [] },
      cat4: { trail: [], extras: [], bundle: [] }
    };
    
    // Initialize once DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }
  
  init() {
    this.setupEventListeners();
    this.loadInitialData();
    
    // Watch for step 7 activation
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('active') && target.dataset.step === '7') {
            setTimeout(() => this.loadInitialData(), 500);
          }
        }
      });
    });
    
    // Observe all quiz steps
    document.querySelectorAll('.quiz-step').forEach(step => {
      observer.observe(step, { attributes: true, attributeFilter: ['class'] });
    });
  }
  
  setupEventListeners() {
    // Listen for custom events from trail box and extras systems
    document.addEventListener('trail-box-updated', (e) => {
      this.syncWithTrailBox(e.detail.catNumber, e.detail.selections);
    });
    
    document.addEventListener('extras-updated', (e) => {
      this.syncWithExtras(e.detail.catNumber, e.detail.extras);
    });
    
    document.addEventListener('bundle-updated', (e) => {
      this.syncWithBundle(e.detail.catNumber, e.detail.selections);
    });
    
    // Listen for cat changes
    document.addEventListener('quiz-cat-changed', (e) => {
      this.renderAllLists();
    });
  }
  
  loadInitialData() {
    // Wait a bit for DOM to be fully ready and other systems to initialize
    setTimeout(() => {
      
      // Load trail box data if available
      const trailBoxBuilder = document.querySelector('trail-box');
      
      if (trailBoxBuilder && trailBoxBuilder.selections) {
        const catCount = window.QuizState?.get('submissionData.catCount') || 1;
        for (let i = 1; i <= catCount; i++) {
          const catKey = `cat${i}`;
          if (trailBoxBuilder.selections[catKey] && trailBoxBuilder.selections[catKey].length > 0) {
            this.syncWithTrailBox(i, trailBoxBuilder.selections[catKey]);
          }
        }
      }
      
      // Load extras data if available
      if (window.QuizExtrasManager) {
        const catCount = window.QuizState?.get('submissionData.catCount') || 1;
        for (let i = 1; i <= catCount; i++) {
          const extras = window.QuizExtrasManager.getExtras(i);
          if (extras && extras.length > 0) {
            this.syncWithExtras(i, extras);
          }
        }
      }
      
      // Load bundle data if available
      const bundleBuilder = document.querySelector('quiz-bundle');
      
      if (bundleBuilder && bundleBuilder.selections) {
        const catCount = window.QuizState?.get('submissionData.catCount') || 1;
        for (let i = 1; i <= catCount; i++) {
          const catKey = `cat${i}`;
          if (bundleBuilder.selections[catKey] && bundleBuilder.selections[catKey].length > 0) {
            this.syncWithBundle(i, bundleBuilder.selections[catKey]);
          }
        }
      }
      
      // Initial render
      this.renderAllLists();
      
      // Ensure monthly lists are also rendered
      this.renderPerCatMonthlyLists();
    }, 100);
  }
  
  syncWithTrailBox(catNumber, selections) {
    const catKey = `cat${catNumber}`;
    if (!this.products[catKey]) return;
    
    
    // Update trail products for this cat
    this.products[catKey].trail = selections.map(selection => {
      // Try to get image from the product card in the trail box
      // Look in both trail-box-builder and trail-box-system (when popup is open)
      let productCard = document.querySelector(`.trail-box-builder [data-product-id="${selection.id}"]`) ||
                       document.querySelector(`.trail-box-system [data-product-id="${selection.id}"]`);
      const imageEl = productCard?.querySelector('.product-image');
      const imageUrl = imageEl?.src || selection.image || null;
      
      
      return {
        id: selection.id,
        variantId: selection.variantId || selection.variant_id,
        title: selection.title,
        quantity: selection.quantity,
        price: selection.price,
        image: imageUrl,
        type: 'trail'
      };
    });
    
    this.renderProductList(catNumber);
  }
  
  syncWithExtras(catNumber, extras) {
    const catKey = `cat${catNumber}`;
    if (!this.products[catKey]) return;
    
    // Update extras for this cat
    this.products[catKey].extras = extras.map(extra => {
      // Try to get image from the extras popup product card
      const productCard = document.querySelector(`.extras-popup-system [data-product-id="${extra.id}"]`);
      const imageEl = productCard?.querySelector('.product-image');
      const imageUrl = imageEl?.src || extra.image || null;
      
      return {
        id: extra.id,
        variantId: extra.variantId,
        title: extra.title,
        quantity: extra.quantity || 1,
        price: extra.price,
        image: imageUrl,
        type: 'extra'
      };
    });
    
    this.renderProductList(catNumber);
  }
  
  syncWithBundle(catNumber, selections) {
    const catKey = `cat${catNumber}`;
    if (!this.products[catKey]) return;
    
    
    // Update bundle products for this cat
    this.products[catKey].bundle = selections.map(selection => {
      // Try to get image from the bundle system product card
      let productCard = document.querySelector(`.quiz-bundle [data-product-id="${selection.id}"]`) ||
                       document.querySelector(`.bundle-system [data-product-id="${selection.id}"]`);
      const imageEl = productCard?.querySelector('.product-image');
      const imageUrl = imageEl?.src || selection.image || null;
      
      
      return {
        id: selection.id,
        variantId: selection.variantId || selection.variant_id,
        title: selection.title,
        quantity: selection.quantity,
        price: selection.price,
        image: imageUrl,
        type: 'bundle'
      };
    });
    
    // Render combined monthly list for all cats (if container exists)
    this.renderCombinedMonthlyProductList();
    
    // Also render individual cat's monthly list
    this.renderPerCatMonthlyLists();
  }
  
  // Remove the fetchProductImage method as we're getting images directly from DOM
  
  getAllProducts(catNumber) {
    const catKey = `cat${catNumber}`;
    if (!this.products[catKey]) return [];
    
    // Combine trail and extras, maintaining order
    return [
      ...this.products[catKey].trail,
      ...this.products[catKey].extras
    ];
  }
  
  renderProductList(catNumber) {
    // Find the container for this cat
    let container = document.querySelector(`.unified-products-list[data-cat="${catNumber}"]`);
    
    // For single cat mode, also check without data-cat attribute
    if (!container && catNumber === 1) {
      container = document.querySelector('.unified-products-list:not([data-cat])');
    }
    
    // Also check in cat-container elements for multi-cat scenarios
    if (!container) {
      const catContainer = document.querySelector(`.cat-container[data-cat="${catNumber}"]`);
      if (catContainer) {
        container = catContainer.querySelector('.unified-products-list');
      }
    }
    
    // Also update the legacy selected-extras-list if it exists
    let legacyContainer = document.querySelector(`.selected-extras-list[data-cat="${catNumber}"]`);
    if (!legacyContainer && catNumber === 1) {
      legacyContainer = document.querySelector('.selected-extras-list:not([data-cat])');
    }
    
    if (!container && !legacyContainer) {
      return;
    }
    
    const products = this.getAllProducts(catNumber);
    
    // Generate HTML for all products
    const html = products.map(product => {
      const imageUrl = product.image || '';
      const quantity = product.quantity || 1;
      
      return `
        <div class="product-list-item" data-product-id="${product.id}" data-type="${product.type}">
          <div class="product-info">
            ${imageUrl ? `
              <img src="${imageUrl}" alt="${product.title}" class="product-thumb">
            ` : `
              <div class="product-thumb-placeholder"></div>
            `}
            <span class="product-title S_Body_Regular">${product.title}</span>
          </div>
          <span class="product-quantity S_Body_Regular">x${quantity}</span>
        </div>
      `;
    }).join('');
    
    // Update both containers if they exist
    if (container) {
      container.innerHTML = html || '<div class="empty-state S_Body_Regular">No products selected</div>';
    }
    
    if (legacyContainer) {
      // For legacy container, render in old format
      const legacyHtml = products.map(product => {
        const price = product.price || 0;
        return `<div class="selected-extra-item S_Body_Regular">${product.title} - £${price.toFixed(2)}</div>`;
      }).join('');
      legacyContainer.innerHTML = legacyHtml;
    }
    
    // Update count displays
    this.updateProductCounts(catNumber);
  }
  
  renderAllLists() {
    const catCount = window.QuizState?.get('submissionData.catCount') || 1;
    for (let i = 1; i <= catCount; i++) {
      this.renderProductList(i);
    }
    // Render monthly list once with all cats' products (if container exists)
    this.renderCombinedMonthlyProductList();
    
    // Also render per-cat monthly lists
    this.renderPerCatMonthlyLists();
  }
  
  renderCombinedMonthlyProductList() {
    // Find the single monthly container (no data-cat attribute)
    const container = document.querySelector('.unified-products-list-monthly');
    
    if (!container) {
      return;
    }
    
    // Also render per-cat monthly lists if they exist
    this.renderPerCatMonthlyLists();
    
    // Gather all cats' bundle products
    const catCount = window.QuizState?.get('submissionData.catCount') || 1;
    let allBundleProducts = [];
    
    for (let i = 1; i <= catCount; i++) {
      const catKey = `cat${i}`;
      const catProducts = this.products[catKey]?.bundle || [];
      
      // Add cat info to each product for grouping/display
      catProducts.forEach(product => {
        allBundleProducts.push({
          ...product,
          catNumber: i,
          catName: window.QuizState?.get(`submissionData.cat${i}.fuzzballer_name`) || `Cat ${i}`
        });
      });
    }
    
    
    // Generate HTML for all bundle products grouped by cat
    let html = '';
    
    // Group products by cat for better organization
    for (let i = 1; i <= catCount; i++) {
      const catProducts = allBundleProducts.filter(p => p.catNumber === i);
      
      if (catProducts.length > 0) {
        // Add cat header if multiple cats
        if (catCount > 1) {
          html += `<div class="cat-products-group" data-cat="${i}">`;
          html += `<h5 class="cat-group-header S_Body_Bold">${catProducts[0].catName}'s products</h5>`;
        }
        
        // Add products for this cat
        catProducts.forEach(product => {
          const imageUrl = product.image || '';
          const quantity = product.quantity || 1;
          
          html += `
            <div class="product-list-item" data-product-id="${product.id}" data-type="${product.type}" data-cat="${i}">
              <div class="product-info">
                ${imageUrl ? `
                  <img src="${imageUrl}" alt="${product.title}" class="product-thumb">
                ` : `
                  <div class="product-thumb-placeholder"></div>
                `}
                <span class="product-title S_Body_Regular">${product.title}</span>
              </div>
              <span class="product-quantity S_Body_Regular">x${quantity}</span>
            </div>
          `;
        });
        
        if (catCount > 1) {
          html += `</div>`;
        }
      }
    }
    
    // Update container
    container.innerHTML = html || '<div class="empty-state S_Body_Regular">No products selected for monthly plan</div>';
    
    // Update monthly totals for all cats
    this.updateCombinedMonthlyTotals();
  }
  
  renderMonthlyProductList(catNumber) {
    // Find the monthly container
    let container = document.querySelector(`.unified-products-list-monthly[data-cat="${catNumber}"]`);
    
    // For single cat mode, also check without data-cat attribute
    if (!container && catNumber === 1) {
      container = document.querySelector('.unified-products-list-monthly:not([data-cat])');
    }
    
    if (!container) {
      return;
    }
    
    const catKey = `cat${catNumber}`;
    const bundleProducts = this.products[catKey]?.bundle || [];
    
    
    // Generate HTML for bundle products (showing actual quantities, not multiplied)
    const html = bundleProducts.map(product => {
      const imageUrl = product.image || '';
      const quantity = product.quantity || 1;
      
      return `
        <div class="product-list-item" data-product-id="${product.id}" data-type="${product.type}">
          <div class="product-info">
            ${imageUrl ? `
              <img src="${imageUrl}" alt="${product.title}" class="product-thumb">
            ` : `
              <div class="product-thumb-placeholder"></div>
            `}
            <span class="product-title S_Body_Regular">${product.title}</span>
          </div>
          <span class="product-quantity S_Body_Regular">x${quantity}</span>
        </div>
      `;
    }).join('');
    
    // Update container
    container.innerHTML = html || '<div class="empty-state S_Body_Regular">No products selected for monthly plan</div>';
    
    // Update monthly total items count
    this.updateMonthlyTotals(catNumber);
  }
  
  updateProductCounts(catNumber) {
    const products = this.getAllProducts(catNumber);
    const trailCount = products.filter(p => p.type === 'trail').reduce((sum, p) => sum + (p.quantity || 1), 0);
    const extrasCount = products.filter(p => p.type === 'extra').reduce((sum, p) => sum + (p.quantity || 1), 0);
    
    // Update any count displays
    const trailCountEl = document.querySelector(`.trail-box-count[data-cat="${catNumber}"]`);
    if (trailCountEl) {
      trailCountEl.textContent = trailCount;
    }
    
    const extrasCountEl = document.querySelector(`.extras-count[data-cat="${catNumber}"]`);
    if (extrasCountEl) {
      extrasCountEl.textContent = extrasCount;
    }
  }
  
  updateCombinedMonthlyTotals() {
    // Calculate totals for all cats combined
    const catCount = window.QuizState?.get('submissionData.catCount') || 1;
    let totalBags = 0;
    let totalTins = 0;
    let totalItemsCount = 0;
    
    for (let i = 1; i <= catCount; i++) {
      const catKey = `cat${i}`;
      const bundleProducts = this.products[catKey]?.bundle || [];
      
      bundleProducts.forEach(product => {
        totalItemsCount += (product.quantity || 1);
        const monthlyQty = (product.quantity || 1) * 4; // 4 weeks per month
        
        // Check if it's wet food (contains tins) or dry food (bags)
        if (product.title && product.title.includes('(7 x 70g)')) {
          // Wet food - each pack has 7 tins
          totalTins += monthlyQty * 7;
        } else if (product.title && product.title.includes('(500g)')) {
          // Dry food - count as bags
          totalBags += monthlyQty;
        }
      });
    }
    
    // Build delivery text
    let deliveryText = '';
    const parts = [];
    if (totalBags > 0) {
      parts.push(`${totalBags} bag${totalBags !== 1 ? 's' : ''}`);
    }
    if (totalTins > 0) {
      parts.push(`${totalTins} tin${totalTins !== 1 ? 's' : ''}`);
    }
    
    if (parts.length > 0) {
      deliveryText = parts.join(', ') + ' in total';
    } else {
      deliveryText = '0 items in total';
    }
    
    // Update delivery total text
    const deliveryTotalEl = document.querySelector('[data-tab-content="monthly"] .total-items-text');
    if (deliveryTotalEl) {
      deliveryTotalEl.textContent = deliveryText;
    }
    
    // Update the total items count
    const totalItemsEl = document.querySelector('[data-tab-content="monthly"] .total-items-count');
    if (totalItemsEl) {
      totalItemsEl.textContent = totalItemsCount;
    }
  }
  
  updateMonthlyTotals(catNumber) {
    const catKey = `cat${catNumber}`;
    const bundleProducts = this.products[catKey]?.bundle || [];
    
    // Calculate total monthly items properly
    let totalBags = 0;
    let totalTins = 0;
    
    bundleProducts.forEach(product => {
      const monthlyQty = (product.quantity || 1) * 4; // 4 weeks per month
      
      // Check if it's wet food (contains tins) or dry food (bags)
      if (product.title && product.title.includes('(7 x 70g)')) {
        // Wet food - each pack has 7 tins
        totalTins += monthlyQty * 7;
      } else if (product.title && product.title.includes('(500g)')) {
        // Dry food - count as bags
        totalBags += monthlyQty;
      }
    });
    
    // Build delivery text
    let deliveryText = '';
    const parts = [];
    if (totalBags > 0) {
      parts.push(`${totalBags} bag${totalBags !== 1 ? 's' : ''}`);
    }
    if (totalTins > 0) {
      parts.push(`${totalTins} tin${totalTins !== 1 ? 's' : ''}`);
    }
    
    if (parts.length > 0) {
      deliveryText = parts.join(', ') + ' in total';
    } else {
      deliveryText = '0 items in total';
    }
    
    // Update delivery total text
    const deliveryTotalEl = document.querySelector('[data-tab-content="monthly"] .total-items-text');
    if (deliveryTotalEl) {
      deliveryTotalEl.textContent = deliveryText;
    }
    
    // Update the total items count (just sum of products, not multiplied by 4)
    const totalItemsCount = bundleProducts.reduce((sum, product) => {
      return sum + (product.quantity || 1);
    }, 0);
    
    const totalItemsEl = document.querySelector('[data-tab-content="monthly"] .total-items-count');
    if (totalItemsEl) {
      totalItemsEl.textContent = totalItemsCount;
    }
  }
  
  // Render monthly products for individual cat sections
  renderPerCatMonthlyLists() {
    const catCount = window.QuizState?.get('submissionData.catCount') || 1;
    
    for (let i = 1; i <= catCount; i++) {
      const catKey = `cat${i}`;
      const bundleProducts = this.products[catKey]?.bundle || [];
      
      // Find the monthly products container for this cat
      const container = document.querySelector(`.monthly-products-list[data-cat="${i}"]`);
      
      if (!container) {
        continue;
      }
      
      
      // Generate HTML for this cat's bundle products
      const html = bundleProducts.map(product => {
        const imageUrl = product.image || '';
        const quantity = product.quantity || 1;
        
        return `
          <div class="product-list-item" data-product-id="${product.id}" data-type="${product.type}">
            <div class="product-info">
              ${imageUrl ? `
                <img src="${imageUrl}" alt="${product.title}" class="product-thumb">
              ` : `
                <div class="product-thumb-placeholder"></div>
              `}
              <span class="product-title S_Body_Regular">${product.title}</span>
            </div>
            <span class="product-quantity S_Body_Regular">x${quantity}</span>
          </div>
        `;
      }).join('');
      
      // Update container
      container.innerHTML = html || '<div class="empty-state S_Body_Regular">No products selected for monthly plan</div>';
    }
  }
  
  // Utility method to trigger a full refresh
  refresh() {
    this.loadInitialData();
  }
}

// Initialize the manager
window.UnifiedProductsManager = new UnifiedProductsManager();